import { Inject, Injectable, NgZone } from '@angular/core';
import { DOCUMENT } from '@angular/core';

/**
 * Corrige roles y nombres accesibles de @ng-select/ng-select:
 * - Quita role="listbox" del host (el combobox real está en el input interno).
 * - Asegura role="listbox" en el contenedor real de opciones.
 * - Asocia label ↔ combobox (aria-labelledby) o usa placeholder como aria-label.
 *
 * El observer solo reacciona a ng-select nuevos o a aria-expanded del combobox.
 * No observa `class` en todo el documento: eso generaba un bucle de mutaciones.
 */
@Injectable({ providedIn: 'root' })
export class NgSelectAccessibilityService {
  private started = false;
  private mutationObserver: MutationObserver | null = null;
  private refreshTimeout: ReturnType<typeof setTimeout> | null = null;
  private isEnhancing = false;
  private readonly placeholderCache = new WeakMap<HTMLElement, string>();
  private readonly enhancedHosts = new WeakSet<HTMLElement>();
  private readonly comboboxSelector = '.ng-input input[role="combobox"]';
  private readonly enhancedFlag = 'data-a11y-ng-select';

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private ngZone: NgZone,
  ) {}

  init(): void {
    if (this.started) {
      return;
    }

    this.started = true;
    this.refreshAll();

    this.ngZone.runOutsideAngular(() => {
      this.mutationObserver = new MutationObserver((mutations) => {
        if (this.isEnhancing || !this.hasRelevantMutation(mutations)) {
          return;
        }

        this.scheduleRefresh();
      });

      this.mutationObserver.observe(this.document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['aria-expanded'],
      });
    });
  }

  destroy(): void {
    if (this.refreshTimeout) {
      clearTimeout(this.refreshTimeout);
      this.refreshTimeout = null;
    }

    this.mutationObserver?.disconnect();
    this.mutationObserver = null;
    this.started = false;
  }

  private hasRelevantMutation(mutations: MutationRecord[]): boolean {
    return mutations.some((mutation) => {
      if (mutation.type === 'attributes') {
        const target = mutation.target;
        return target instanceof HTMLElement && !!target.closest('ng-select');
      }

      return this.containsNgSelectNode(mutation.addedNodes);
    });
  }

  private containsNgSelectNode(nodes: NodeList): boolean {
    return Array.from(nodes).some((node) => {
      if (!(node instanceof HTMLElement)) {
        return false;
      }

      return (
        node.tagName === 'NG-SELECT' ||
        !!node.querySelector?.('ng-select, .ng-dropdown-panel') ||
        node.classList.contains('ng-dropdown-panel')
      );
    });
  }

  private scheduleRefresh(): void {
    if (this.refreshTimeout) {
      clearTimeout(this.refreshTimeout);
    }

    this.refreshTimeout = setTimeout(() => {
      this.refreshTimeout = null;
      this.refreshAll();
    }, 50);
  }

  private refreshAll(): void {
    this.isEnhancing = true;

    try {
      this.document.querySelectorAll('ng-select').forEach((host) => {
        this.enhance(host as HTMLElement);
      });
    } finally {
      this.isEnhancing = false;
    }
  }

  private enhance(host: HTMLElement): void {
    if (host.getAttribute('role') === 'listbox') {
      host.removeAttribute('role');
    }

    host.removeAttribute('aria-label');
    host.removeAttribute('aria-labelledby');

    const combobox = host.querySelector(this.comboboxSelector) as HTMLInputElement | null;
    if (!combobox) {
      return;
    }

    this.fixDropdownPanel(host);

    const alreadyEnhanced =
      this.enhancedHosts.has(host) || host.getAttribute(this.enhancedFlag) === 'true';
    if (!alreadyEnhanced) {
      this.applyAccessibleName(host, combobox);
      combobox.setAttribute('aria-haspopup', 'listbox');
      host.setAttribute(this.enhancedFlag, 'true');
      this.enhancedHosts.add(host);
    }
  }

  private fixDropdownPanel(host: HTMLElement): void {
    const panel = host.querySelector('.ng-dropdown-panel');
    if (panel?.getAttribute('role') === 'listbox') {
      panel.removeAttribute('role');
    }

    const listbox = host.querySelector('.ng-dropdown-panel-items');
    if (listbox && listbox.getAttribute('role') !== 'listbox') {
      listbox.setAttribute('role', 'listbox');
    }
  }

  private applyAccessibleName(host: HTMLElement, combobox: HTMLInputElement): void {
    const label = this.findAssociatedLabel(host);

    if (label) {
      this.wireLabelToCombobox(label, combobox);
      combobox.removeAttribute('aria-label');
      return;
    }

    if (combobox.getAttribute('aria-labelledby')) {
      combobox.removeAttribute('aria-label');
      return;
    }

    if (combobox.getAttribute('aria-label')) {
      return;
    }

    const placeholder = this.getPlaceholder(host);
    if (placeholder) {
      combobox.setAttribute('aria-label', placeholder);
    }
  }

  private findAssociatedLabel(host: HTMLElement): HTMLLabelElement | null {
    const groupInput = host.closest('.group-input');
    if (!groupInput) {
      return null;
    }

    const directLabel = groupInput.querySelector(':scope > label');
    return directLabel ? (directLabel as HTMLLabelElement) : null;
  }

  private wireLabelToCombobox(label: HTMLLabelElement, combobox: HTMLInputElement): void {
    if (!label.id) {
      label.id = this.createStableId('ng-select-label');
    }

    if (!combobox.id) {
      if (label.htmlFor) {
        combobox.id = label.htmlFor;
      } else {
        combobox.id = this.createStableId('ng-select');
        label.htmlFor = combobox.id;
      }
    } else if (!label.htmlFor) {
      label.htmlFor = combobox.id;
    }

    combobox.setAttribute('aria-labelledby', label.id);
  }

  private getPlaceholder(host: HTMLElement): string | null {
    const cached = this.placeholderCache.get(host);
    if (cached) {
      return cached;
    }

    const placeholderEl = host.querySelector('.ng-placeholder');
    const text = placeholderEl?.textContent?.trim();
    if (text) {
      this.placeholderCache.set(host, text);
      return text;
    }

    return null;
  }

  private createStableId(prefix: string): string {
    return `${prefix}-${Math.random().toString(36).slice(2, 9)}`;
  }
}
